<template>
  <div style="position: relative">
    <!-- Botón de edición -->
    <v-tooltip bottom>
      <template v-slot:activator="{ on }">
        <v-btn icon small v-on="on" @click="openModal" color="orange">
          <v-icon small>mdi-clock-edit</v-icon>
        </v-btn>
      </template>
      <span>Editar horarios</span>
    </v-tooltip>

    <!-- Modal de edición -->
    <v-dialog v-model="isModalOpen" max-width="600px" persistent>
      <v-card>
        <v-card-title class="headline">
          <v-icon color="primary" left>mdi-clock-edit</v-icon>
          Editar Horarios - {{ item.WkrName }}
        </v-card-title>

        <v-card-text>
          <v-container>
            <!-- ✅ NUEVO: Switch para habilitar/deshabilitar horario laboral -->
            <v-row>
              <v-col cols="12">
                <v-switch
                  v-model="hasLaborSchedule"
                  label="¿Tiene horario laboral programado?"
                  color="primary"
                  style="margin-bottom: 16px"
                />
              </v-col>
            </v-row>

            <!-- Horario Laboral (solo si está habilitado) -->
            <template v-if="hasLaborSchedule">
              <v-row>
                <v-col cols="12">
                  <h3 style="color: #1976d2; margin-bottom: 16px">
                    <v-icon color="blue">mdi-briefcase</v-icon>
                    Horario Laboral
                  </h3>
                </v-col>
              </v-row>

              <!-- Entrada Laboral -->
              <v-row>
                <v-col cols="6">
                  <s-date
                    label="Fecha Entrada"
                    v-model="laborStart.date"
                    :max="today"
                    disabled
                  />
                </v-col>
                <v-col cols="6">
                  <s-text
                    type="time"
                    v-model="laborStart.time"
                    full-width
                    label="Hora Entrada"
                  />
                </v-col>
              </v-row>

              <!-- Salida Laboral -->
              <v-row>
                <v-col cols="6">
                  <s-date
                    v-model="laborEnd.date"
                    full-width
                    :min="laborStart.date"
                    :max="
                      new Date(
                        new Date(laborStart.date).getTime() + 24 * 60 * 60 * 1000
                      )
                        .toISOString()
                        .substr(0, 10)
                    "
                    label="Fecha Salida"
                  />
                </v-col>
                <v-col cols="6">
                  <s-text
                    type="time"
                    v-model="laborEnd.time"
                    full-width
                    label="Hora Salida"
                  />
                </v-col>
              </v-row>

              <v-row v-if="laborHoursInfo.isValid">
                <v-col cols="12">
                  <v-card
                    outlined
                    style="
                      padding: 12px;
                      background: #f8f9fa;
                      margin-bottom: 16px;
                    "
                  >
                    <div style="display: flex; align-items: center; gap: 12px">
                      <v-icon color="primary">mdi-clock-outline</v-icon>
                      <div>
                        <strong style="color: #1976d2">Horas Laborales:</strong>
                        <span
                          style="
                            font-size: 18px;
                            margin-left: 8px;
                            color: #2e7d32;
                          "
                        >
                          {{ laborHoursInfo.totalHours }}
                        </span>
                      </div>

                      <!-- Mostrar detalle si es horario nocturno -->
                      <div
                        v-if="laborHoursInfo.isNightShift"
                        style="margin-left: auto"
                      >
                        <v-chip color="purple" text-color="white" small>
                          <v-icon left small>mdi-weather-night</v-icon>
                          Horario Nocturno
                        </v-chip>
                      </div>
                    </div>

                    <!-- Detalle del cálculo -->
                    <div
                      v-if="laborHoursInfo.breakdown"
                      style="margin-top: 8px; font-size: 12px; color: #666"
                    >
                      {{ laborHoursInfo.breakdown }}
                    </div>
                  </v-card>
                </v-col>
              </v-row>

              <!-- Separador -->
              <v-divider style="margin: 24px 0"></v-divider>

              <!-- Horario Refrigerio (solo si hay horario laboral) -->
              <v-row>
                <v-col cols="6">
                  <h3 style="color: #ff9800; margin-bottom: 16px">
                    <v-icon color="orange">mdi-coffee</v-icon>
                    Horario Refrigerio
                  </h3>
                </v-col>

                <v-col cols="6">
                  <v-switch
                    style="margin: 0; padding: 0"
                    v-model="hasBreak"
                    label="¿Tiene horario de refrigerio?"
                    color="orange"
                  />
                </v-col>
              </v-row>

              <!-- ✅ MEJORADO: Fechas de refrigerio con lógica nocturna -->
              <!-- Entrada Refrigerio -->
              <v-row v-if="hasBreak">
                <v-col cols="6">
                  <s-date
                    label="Fecha Inicio Refrigerio"
                    v-model="breakStart.date"
                    :min="laborStart.date"
                    :max="laborEnd.date"
                  />
                </v-col>
                <v-col cols="6">
                  <s-text
                    type="time"
                    v-model="breakStart.time"
                    full-width
                    label="Hora Inicio Refrigerio"
                  />
                </v-col>
              </v-row>

              <!-- Salida Refrigerio -->
              <v-row v-if="hasBreak">
                <v-col cols="6">
                  <s-date
                    label="Fecha Fin Refrigerio"
                    v-model="breakEnd.date"
                    :min="breakStart.date"
                    :max="laborEnd.date"
                  />
                </v-col>
                <v-col cols="6">
                  <s-text
                    type="time"
                    v-model="breakEnd.time"
                    full-width
                    label="Hora Fin Refrigerio"
                  />
                </v-col>
              </v-row>

              <v-row v-if="hasBreak && breakHoursInfo.isValid">
                <v-col cols="12">
                  <v-card
                    outlined
                    style="
                      padding: 12px;
                      background: #fff3e0;
                      margin-bottom: 16px;
                    "
                  >
                    <div style="display: flex; align-items: center; gap: 12px">
                      <v-icon color="orange">mdi-coffee-outline</v-icon>
                      <div>
                        <strong style="color: #f57c00"
                          >Tiempo de Refrigerio:</strong
                        >
                        <span
                          style="
                            font-size: 16px;
                            margin-left: 8px;
                            color: #e65100;
                          "
                        >
                          {{ breakHoursInfo.totalHours }}
                        </span>
                      </div>
                    </div>

                    <!-- Detalle del cálculo -->
                    <div
                      v-if="breakHoursInfo.breakdown"
                      style="margin-top: 8px; font-size: 12px; color: #666"
                    >
                      {{ breakHoursInfo.breakdown }}
                    </div>
                  </v-card>
                </v-col>
              </v-row>
            </template>

            <!-- ✅ MENSAJE cuando no hay horario laboral -->
            <v-row v-else>
              <v-col cols="12">
                <v-alert type="info" outlined>
                  <v-icon left>mdi-information</v-icon>
                  Sin horario laboral programado. Se tomara como Descanso.
                </v-alert>
              </v-col>
            </v-row>

            <!-- Resumen de validaciones -->
            <v-alert
              v-if="validationErrors.length > 0"
              type="warning"
              style="margin-top: 16px"
            >
              <h4>⚠️ Advertencias:</h4>
              <ul>
                <li v-for="error in validationErrors" :key="error">
                  {{ error }}
                </li>
              </ul>
            </v-alert>
          </v-container>
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn
            color="success"
            @click="save"
            :loading="saving"
            :disabled="hasLaborSchedule && validationErrors.length > 0"
          >
            <v-icon left>mdi-content-save</v-icon>
            Guardar
          </v-btn>
          <v-btn text @click="cancel">
            <v-icon left>mdi-close</v-icon>
            Cancelar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import _sHmnHoursWorkedService from "@/services/HumanResource/HmnHoursWorkedService.js";

export default {
  name: "InlineScheduleEditor",
  props: {
    item: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      isModalOpen: false,
      saving: false,
      hasLaborSchedule: true, // ✅ NUEVO: Control de horario laboral
      hasBreak: false,

      // Horario laboral
      laborStart: {
        date: null,
        time: null,
      },
      laborEnd: {
        date: null,
        time: null,
      },

      // Horario refrigerio
      breakStart: {
        date: null,
        time: null,
      },
      breakEnd: {
        date: null,
        time: null,
      },

      // Valores originales para rollback
      originalValues: {},
    };
  },
  computed: {
    today() {
      return new Date().toISOString().substr(0, 10);
    },
    laborHoursInfo() {
      if (
        !this.hasLaborSchedule ||
        !this.laborStart.date ||
        !this.laborStart.time ||
        !this.laborEnd.date ||
        !this.laborEnd.time
      ) {
        return { isValid: false };
      }

      const startDateTime = new Date(
        `${this.laborStart.date}T${this.laborStart.time}`
      );
      const endDateTime = new Date(
        `${this.laborEnd.date}T${this.laborEnd.time}`
      );

      if (startDateTime >= endDateTime) {
        return { isValid: false };
      }

      const diffMs = endDateTime - startDateTime;
      const diffHours = diffMs / (1000 * 60 * 60);
      const hours = Math.floor(diffHours);
      const minutes = Math.round((diffHours - hours) * 60);

      const isNightShift = this.laborStart.date !== this.laborEnd.date;

      let totalHours = "";
      if (minutes === 0) {
        totalHours = `${hours}h`;
      } else {
        totalHours = `${hours}h ${minutes}m`;
      }

      let breakdown = "";
      if (isNightShift) {
        breakdown = `Desde ${this.formatDateTime(
          startDateTime
        )} hasta ${this.formatDateTime(endDateTime)}`;
      } else {
        breakdown = `Desde ${this.laborStart.time} hasta ${this.laborEnd.time}`;
      }

      return {
        isValid: true,
        totalHours,
        isNightShift,
        breakdown,
        hours: diffHours,
      };
    },

    breakHoursInfo() {
      if (
        !this.hasLaborSchedule ||
        !this.hasBreak ||
        !this.breakStart.date ||
        !this.breakStart.time ||
        !this.breakEnd.date ||
        !this.breakEnd.time
      ) {
        return { isValid: false };
      }

      const startDateTime = new Date(
        `${this.breakStart.date}T${this.breakStart.time}`
      );
      const endDateTime = new Date(
        `${this.breakEnd.date}T${this.breakEnd.time}`
      );

      if (startDateTime >= endDateTime) {
        return { isValid: false };
      }

      const diffMs = endDateTime - startDateTime;
      const diffHours = diffMs / (1000 * 60 * 60);
      const hours = Math.floor(diffHours);
      const minutes = Math.round((diffHours - hours) * 60);

      let totalHours = "";
      if (minutes === 0) {
        totalHours = `${hours}h`;
      } else {
        totalHours = `${hours}h ${minutes}m`;
      }

      const breakdown = `Desde ${this.breakStart.time} hasta ${this.breakEnd.time}`;

      return {
        isValid: true,
        totalHours,
        breakdown,
        hours: diffHours,
      };
    },

    validationErrors() {
      const errors = [];

      // Solo validar si el horario laboral está habilitado
      if (!this.hasLaborSchedule) {
        return []; // Sin validaciones si no hay horario
      }

      // Validar que fecha/hora laboral sean válidas
      if (
        this.laborStart.date &&
        this.laborStart.time &&
        this.laborEnd.date &&
        this.laborEnd.time
      ) {
        const startDateTime = new Date(
          `${this.laborStart.date}T${this.laborStart.time}`
        );
        const endDateTime = new Date(
          `${this.laborEnd.date}T${this.laborEnd.time}`
        );

        if (startDateTime >= endDateTime) {
          errors.push(
            "La hora de salida debe ser posterior a la hora de entrada"
          );
        }

        // Validar duración máxima (ej: no más de 24 horas)
        const diffHours = (endDateTime - startDateTime) / (1000 * 60 * 60);
        if (diffHours > 24) {
          errors.push("El horario laboral no puede exceder 24 horas");
        }
      }

      // Validar refrigerio si está habilitado
      if (
        this.hasBreak &&
        this.breakStart.date &&
        this.breakStart.time &&
        this.breakEnd.date &&
        this.breakEnd.time
      ) {
        const breakStartDateTime = new Date(
          `${this.breakStart.date}T${this.breakStart.time}`
        );
        const breakEndDateTime = new Date(
          `${this.breakEnd.date}T${this.breakEnd.time}`
        );
        const laborStartDateTime = new Date(
          `${this.laborStart.date}T${this.laborStart.time}`
        );
        const laborEndDateTime = new Date(
          `${this.laborEnd.date}T${this.laborEnd.time}`
        );

        if (breakStartDateTime >= breakEndDateTime) {
          errors.push(
            "La hora de fin de refrigerio debe ser posterior al inicio"
          );
        }

        if (breakStartDateTime < laborStartDateTime) {
          errors.push(
            "El refrigerio no puede iniciar antes del horario laboral"
          );
        }

        if (breakEndDateTime > laborEndDateTime) {
          errors.push(
            "El refrigerio no puede terminar después del horario laboral"
          );
        }
      }

      return errors;
    },
  },
  watch: {
    // ✅ NUEVO: Watcher para ajustar fechas de refrigerio automáticamente
    laborHoursInfo: {
      handler(newVal) {
        if (newVal.isValid && this.hasBreak) {
          this.adjustBreakDates();
        }
      },
      deep: true
    },
    
    hasLaborSchedule(newVal) {
      if (!newVal) {
        // Si deshabilita horario laboral, deshabilitar también refrigerio
        this.hasBreak = false;
      }
    }
  },
  methods: {
    openModal() {
      this.loadCurrentValues();
      this.isModalOpen = true;
    },

    loadCurrentValues() {
      // ✅ MEJORADO: Detectar si hay horario laboral
      this.hasLaborSchedule = !!(this.item.HLAB_SddHourBegin && this.item.HLAB_SddHourEnd);

      // Cargar valores actuales del item
      if (this.hasLaborSchedule) {
        const startDate = new Date(this.item.HLAB_SddHourBegin);
        this.laborStart = {
          date: startDate.toISOString().substr(0, 10),
          time: startDate.toTimeString().substr(0, 5),
        };

        const endDate = new Date(this.item.HLAB_SddHourEnd);
        this.laborEnd = {
          date: endDate.toISOString().substr(0, 10),
          time: endDate.toTimeString().substr(0, 5),
        };

        // ✅ CORREGIDO: Cargar refrigerio con fechas correctas
        this.hasBreak = !!(this.item.HREF_SddHourBegin && this.item.HREF_SddHourEnd);

        if (this.hasBreak) {
          const breakStartDate = new Date(this.item.HREF_SddHourBegin);
          const breakEndDate = new Date(this.item.HREF_SddHourEnd);
          
          this.breakStart = {
            date: breakStartDate.toISOString().substr(0, 10),
            time: breakStartDate.toTimeString().substr(0, 5),
          };

          this.breakEnd = {
            date: breakEndDate.toISOString().substr(0, 10),
            time: breakEndDate.toTimeString().substr(0, 5),
          };

          console.log('🔍 Refrigerio cargado:', {
            original_start: this.item.HREF_SddHourBegin,
            original_end: this.item.HREF_SddHourEnd,
            parsed_start: this.breakStart,
            parsed_end: this.breakEnd
          });
        }
      }

      // Guardar valores originales para rollback
      this.originalValues = {
        HLAB_SddHourBegin: this.item.HLAB_SddHourBegin,
        HLAB_SddHourEnd: this.item.HLAB_SddHourEnd,
        HREF_SddHourBegin: this.item.HREF_SddHourBegin,
        HREF_SddHourEnd: this.item.HREF_SddHourEnd,
      };
    },

    // ✅ NUEVO: Ajustar fechas de refrigerio según horario laboral
    adjustBreakDates() {
      if (!this.laborHoursInfo.isValid || !this.hasBreak) return;

      // Si el horario es nocturno y las fechas de refrigerio no están configuradas
      if (this.laborHoursInfo.isNightShift) {
        // Si no hay fecha de inicio de refrigerio, usar la fecha de inicio laboral
        if (!this.breakStart.date) {
          this.breakStart.date = this.laborStart.date;
        }
        
        // Si no hay fecha de fin de refrigerio, usar la fecha de fin laboral
        if (!this.breakEnd.date) {
          this.breakEnd.date = this.laborEnd.date;
        }
      } else {
        // Para horarios normales, usar la misma fecha
        if (!this.breakStart.date) {
          this.breakStart.date = this.laborStart.date;
        }
        if (!this.breakEnd.date) {
          this.breakEnd.date = this.laborStart.date; // Mismo día
        }
      }
    },

    async save() {
      // ✅ MEJORADO: Permitir guardar sin validaciones si no hay horario laboral
      if (this.hasLaborSchedule && this.validationErrors.length > 0) {
        this.$fun.alert(
          "Por favor corrija los errores antes de guardar",
          "warning"
        );
        return;
      }

      this.saving = true;

      try {
        let HLAB_Begin = null;
        let HLAB_End = null;
        let HREF_Begin = null;
        let HREF_End = null;

        // ✅ SOLO crear fechas si el horario laboral está habilitado
        if (this.hasLaborSchedule) {
          const formatLocalDateTime = (date, time) => {
            return `${date}T${time}:00`;
          };

          HLAB_Begin = formatLocalDateTime(
            this.laborStart.date,
            this.laborStart.time
          );
          HLAB_End = formatLocalDateTime(
            this.laborEnd.date,
            this.laborEnd.time
          );

          if (this.hasBreak) {
            HREF_Begin = formatLocalDateTime(
              this.breakStart.date,
              this.breakStart.time
            );
            HREF_End = formatLocalDateTime(
              this.breakEnd.date,
              this.breakEnd.time
            );
          }
        }

        console.log('💾 Enviando al servidor:', {
          hasLaborSchedule: this.hasLaborSchedule,
          HLAB_Begin,
          HLAB_End,
          hasBreak: this.hasBreak,
          HREF_Begin,
          HREF_End
        });

        const updatedSchedule = {
          CacheID: this.item.CacheID,
          HLAB_SddHourBegin: HLAB_Begin, // NULL si no hay horario
          HLAB_SddHourEnd: HLAB_End,     // NULL si no hay horario
          HREF_SddHourBegin: HREF_Begin, // NULL si no hay refrigerio
          HREF_SddHourEnd: HREF_End,     // NULL si no hay refrigerio
          RequestID: this.$fun.getUserID(),
        };

        await _sHmnHoursWorkedService.updateScheduleHours(
          this.item.CacheID,
          updatedSchedule,
          this.$fun.getUserID()
        );

        // Actualizar localmente
        this.item.HLAB_SddHourBegin = HLAB_Begin;
        this.item.HLAB_SddHourEnd = HLAB_End;
        this.item.HREF_SddHourBegin = HREF_Begin;
        this.item.HREF_SddHourEnd = HREF_End;

        this.$emit("save", {
          item: this.item,
          newValues: updatedSchedule,
          oldValues: this.originalValues,
        });

        const message = this.hasLaborSchedule 
          ? "Horarios actualizados correctamente" 
          : "Horario laboral deshabilitado correctamente";
        
        this.$fun.alert(message, "success");
        this.isModalOpen = false;
      } catch (error) {
        console.error("Error al actualizar horarios:", error);
        this.$fun.alert(error.message, "error");

        // Rollback local
        this.item.HLAB_SddHourBegin = this.originalValues.HLAB_SddHourBegin;
        this.item.HLAB_SddHourEnd = this.originalValues.HLAB_SddHourEnd;
        this.item.HREF_SddHourBegin = this.originalValues.HREF_SddHourBegin;
        this.item.HREF_SddHourEnd = this.originalValues.HREF_SddHourEnd;
      } finally {
        this.saving = false;
      }
    },

    formatDateTime(dateTime) {
      const options = {
        weekday: "short",
        day: "2-digit",
        month: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      };
      return dateTime.toLocaleDateString('es-ES', options);
    },

    cancel() {
      this.isModalOpen = false;
      // Resetear valores
      this.laborStart = { date: null, time: null };
      this.laborEnd = { date: null, time: null };
      this.breakStart = { date: null, time: null };
      this.breakEnd = { date: null, time: null };
      this.hasLaborSchedule = true;
      this.hasBreak = false;
    },
  },
};
</script>